import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import {
  type ElementOrigin,
  type MetadataPage,
  browseMetadata,
  tracePath,
} from "./profileClient";
import { useLatestRequest } from "./latest";

export interface SectionView {
  section: string;
  label: string;
  columns: string[];
}

/** The searchable views of a profile version (task 4070.7), in Maximo's own column names: one
 * for every section the metadata API serves (environment.METADATA_SECTIONS). */
export const SECTION_VIEWS: SectionView[] = [
  {
    section: "objects",
    label: "Objects",
    columns: ["objectname", "description", "siteorgtype", "persistent", "entityname", "userdefined"],
  },
  {
    section: "attributes",
    label: "Attributes",
    columns: [
      "objectname",
      "attributename",
      "maxtype",
      "length",
      "scale",
      "required",
      "persistent",
      "writable",
      "domainid",
    ],
  },
  {
    section: "domains",
    label: "Domains",
    columns: ["domainid", "domaintype", "maxtype", "length", "description"],
  },
  {
    section: "domain_values",
    label: "Domain values",
    columns: ["domainid", "value", "maxvalue", "orgid", "siteid", "description"],
  },
  {
    section: "domain_ranges",
    label: "Domain ranges",
    columns: [
      "domainid",
      "rangesegment",
      "rangeminimum",
      "rangemaximum",
      "rangeinterval",
      "orgid",
      "siteid",
    ],
  },
  {
    section: "relationships",
    label: "Relationships",
    columns: ["parent", "name", "child", "cardinality", "whereclause"],
  },
  {
    section: "classifications",
    label: "Classifications",
    columns: ["classstructureid", "classificationid", "parent", "description", "orgid", "siteid"],
  },
  {
    section: "classification_use_with",
    label: "Classification use",
    columns: ["classstructureid", "objectname"],
  },
  {
    section: "specification_attributes",
    label: "Specifications",
    columns: ["classstructureid", "assetattrid", "datatype", "domainid", "mandatory", "measureunitid"],
  },
  {
    section: "object_structures",
    label: "Object structures",
    columns: ["intobjectname", "description", "usewith", "userdefined"],
  },
  {
    section: "object_structure_sources",
    label: "Object structure sources",
    columns: ["intobjectname", "objectname", "parentobjname", "relation", "objectorder"],
  },
  {
    section: "integration_channels",
    label: "Integration channels",
    columns: ["ifacename", "intobjectname", "direction", "description"],
  },
  {
    section: "security_restrictions",
    label: "Security restrictions",
    columns: ["objectname", "attributename", "restricttype", "groupname", "app", "conditionnum"],
  },
  {
    section: "automation",
    label: "Automation",
    columns: [
      "autoscript",
      "launchpointname",
      "launchpointtype",
      "objectname",
      "attributename",
      "active",
      "description",
    ],
  },
  {
    section: "extension_rules",
    label: "Extension rules",
    columns: ["prefix", "component_code", "applies_to", "description"],
  },
  {
    section: "custom_properties",
    label: "Custom properties",
    columns: ["target_kind", "target_key", "property", "value"],
  },
];

const PAGE = 50;

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

export function cell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function originLabel(origin: ElementOrigin | null): string {
  if (!origin) return "";
  if (origin.origin === "extension") {
    const kind = origin.component_kind ?? "unknown component";
    return `extension: ${origin.component_code} (${kind})`;
  }
  return origin.origin === "customer_defined" ? "customer-defined" : "product";
}

/** One section of a profile version: searched across every value, paged, each element with its
 * origin. A section that was not captured says so: missing rows there are unknown, not absent. */
export function SectionTable({ snapshotId, view }: { snapshotId: string; view: SectionView }) {
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState<MetadataPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const begin = useLatestRequest();

  const load = useCallback(
    async (search: string, offset: number) => {
      const current = begin();
      setError(null);
      try {
        const found = await browseMetadata(snapshotId, view.section, {
          q: search || undefined,
          offset,
          limit: PAGE,
        });
        if (!current()) return;
        setPage(found);
        setApplied(search);
      } catch (caught) {
        if (current()) setError(message(caught));
      }
    },
    [snapshotId, view.section, begin],
  );

  useEffect(() => {
    setQ("");
    void load("", 0);
  }, [load]);

  function onSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void load(q.trim(), 0);
  }

  const traceable = view.section === "attributes";
  const withOrigin = page?.rows.some((row) => row.origin !== null) ?? false;

  return (
    <div>
      <form onSubmit={onSearch} role="search">
        <label>
          Search {view.label.toLowerCase()}{" "}
          <input value={q} onChange={(event) => setQ(event.target.value)} />
        </label>{" "}
        <button type="submit">Search</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {page && page.status === "unknown" && (
        <p>
          {view.label} were not captured in {page.profile_version}: anything not listed here is
          unknown, not absent.
        </p>
      )}
      {page && (
        <>
          <p>
            {page.total === 0
              ? `No ${view.label.toLowerCase()} match.`
              : `${page.offset + 1}–${page.offset + page.rows.length} of ${page.total} ` +
                `${view.label.toLowerCase()}`}
          </p>
          {page.rows.length > 0 && (
            <table className="grid">
              <caption>
                {view.label} in {page.profile_version}
              </caption>
              <thead>
                <tr>
                  {view.columns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                  {withOrigin && <th>origin</th>}
                  {traceable && <th />}
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <tr key={row.row_key}>
                    {view.columns.map((column) => (
                      <td key={column}>{cell(row.data[column])}</td>
                    ))}
                    {withOrigin && <td title={row.origin?.basis}>{originLabel(row.origin)}</td>}
                    {traceable && (
                      <td>
                        <Link
                          to={tracePath(
                            snapshotId,
                            String(row.data.objectname),
                            String(row.data.attributename),
                          )}
                        >
                          Trace {String(row.data.objectname)}.{String(row.data.attributename)}
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button
            type="button"
            disabled={page.offset === 0}
            onClick={() => void load(applied, Math.max(0, page.offset - PAGE))}
          >
            Previous
          </button>{" "}
          <button
            type="button"
            disabled={page.offset + page.rows.length >= page.total}
            onClick={() => void load(applied, page.offset + PAGE)}
          >
            Next
          </button>
        </>
      )}
    </div>
  );
}
